import axios from 'axios';
import { api_endpoint } from '../components/constants/constant';

// ─── Generic helpers ───────────────────────────────────────────────────────

const authHeaders = useToken => {
    const headers = { 'Content-Type': 'application/json' };
    if (useToken) {
        headers['Authorization'] = `Bearer ${useToken}`;
    }
    return headers;
};

async function get(path, token, params) {
    try {
        console.log('[api] GET', path, { token: !!token, params });
    } catch {
        // ignore logging errors in environments that may block console
    }
    const { data } = await axios.get(`${api_endpoint}${path}`, {
        headers: authHeaders(token),
        params,
    });
    return data;
}

async function getPublic(path, params) {
    const { data } = await axios.get(`${api_endpoint}${path}`, { params });
    return data;
}

async function post(path, token, body) {
    const { data } = await axios.post(`${api_endpoint}${path}`, body, {
        headers: authHeaders(token),
    });
    return data;
}

export const api = {
    // Public
    getCourseBySlug: courseSlug => getPublic(`/course/slug/${courseSlug}`),

    getCoursesList: params => getPublic(`/course/`, params),

    getCourseContent: (courseSlug, token) => get(`/course/${courseSlug}/content`, token),

    getCourseInstructors: (courseId, token) => get(`/course/${courseId}/instructors`, token),

    checkEnrollment: (courseId, token) => get(`/course/check-enrollment/${courseId}`, token),

    getUserCourses: (token, params) => get(`/course/user-courses`, token, params),

    enrollCourse: (token, courseId, userId) => post(`/course/enroll`, token, { course_id: courseId, user_id: userId }),

    // Lesson / completions
    getLessonCompletions: (userId, courseId, token) => get(`/lesson/completed/${userId}/${courseId}`, token),

    getProjectCompletions: (userId, courseId, token) => get(`/project/completed/${userId}`, token, { course_id: courseId }),

    getQuizCompletions: (userId, courseId, token) => get(`/quiz/completed/${userId}`, token, { course_id: courseId }),

    // Projects
    getProject: (projectId, token) => get(`/project/get/${projectId}`, token),

    getUserProjectSubmissions: (userId, token, params) => get(`/project/user-submissions/${userId}`, token, params),

    getUserProjectStats: token => get(`/project/user-stats`, token),

    getProjectReviews: (token, body) => post(`/project/get/reviews`, token, body),

    submitProject: (token, payload) => post(`/project/submit`, token, payload),

    // Quiz
    getQuizQuestions: (quizId, token) => get(`/quiz/questions/${quizId}`, token),

    getQuizAttempts: (quizId, userId, token) => get(`/quiz/attempts/${quizId}`, token, { user_id: userId }),

    createQuizAttempt: (token, attemptData) => post(`/quiz/attempt/create`, token, attemptData),

    // Lesson actions
    markLessonComplete: (token, lessonId) => post(`/lesson/complete/${lessonId}`, token, {}),

    reviewLesson: (token, payload) => post(`/lesson/review`, token, payload),

    // Pathways
    getUserPathwayEnrollments: token => get(`/pathway/user/enrollments`, token),

    getPathwayProgress: (userId, pathwayId, token) => get(`/pathway/progress/${userId}/${pathwayId}`, token),

    getPathwayCourses: (pathwayId, token, params) => get(`/pathway/${pathwayId}/courses`, token, params),

    getPathwayGradebook: (userId, pathwayId, token) => get(`/pathway/gradebook/${userId}/${pathwayId}`, token),

    // Cohorts
    getUserCohortEnrollments: (userId, token) => get(`/cohort/user-enrolments/${userId}`, token),

    getCohortLeaderboard: (token, params) => get(`/cohort/enrolments/leaderboard/all`, token, params),

    getCohortEnrollments: (cohortId, token, params) => get(`/cohort/enrolments/${cohortId}`, token, params),

    // Live Classes
    getUpcomingSessions: (token, params) => get(`/liveclass/upcoming-sessions`, token, params),

    getOngoingSessions: token => get(`/liveclass/ongoing-sessions`, token),

    getAttendanceLogs: (userId, token, params) => get(`/liveclass/attendance-logs/${userId}`, token, params),

    // Gamification
    getUserXp: (userId, token) => get(`/gamification/user-xp/${userId}`, token),

    getUserStreak: (userId, token) => get(`/gamification/user-streak/${userId}`, token),

    getUserBadgeProgress: (userId, token) => get(`/gamification/user-badge-progress/${userId}`, token),

    getUserStats: token => get(`/gamification/user-stats`, token),

    // Notifications
    getUserNotifications: (token, params) => get(`/notification/user-notifications`, token, params),

    // Course reviews
    getCourseReviews: (courseId, token, params) => get(`/course/reviews/${courseId}`, token, params),
    postCourseReview: (token, body) => post(`/course/review`, token, body),

    // Users
    getUserRoles: (userId, token) => get(`/user/${userId}/roles`, token),
    getUsersByRole: (role, token) => get(`/user/users/role/${role}`, token),

    markNotificationsRead: (token, notificationIds) =>
        post(`/notification/mark-as-read`, token, { notification_ids: notificationIds }),
};

export default api;
